import { useState } from "react";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useAuditLog } from "@/api/queries";
import { Card, CardBody } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";

const ACTION_LABEL: Record<string, { label: string; tone: "accent" | "success" | "warning" | "danger" | "info" | "neutral" }> = {
  "vpn_profile.create": { label: "Выпуск VPN-профиля", tone: "success" },
  "vpn_profile.revoke": { label: "Отзыв VPN-профиля", tone: "warning" },
  "vpn_profile.download": { label: "Скачивание конфига", tone: "info" },
  "client.create": { label: "Создание пользователя", tone: "success" },
  "client.revoke": { label: "Отзыв пользователя", tone: "warning" },
  "client.bulk_import": { label: "Массовый импорт", tone: "info" },
  "tenant.create": { label: "Создание организации", tone: "success" },
  "tenant.update": { label: "Изменение организации", tone: "info" },
  "admin.create": { label: "Создание админа", tone: "success" },
  "admin.update": { label: "Изменение админа", tone: "info" },
  "admin.delete": { label: "Удаление админа", tone: "danger" },
  "admin.reset_password": { label: "Сброс пароля", tone: "warning" },
  "admin.login": { label: "Вход админа", tone: "neutral" },
  "admin.mfa_enable": { label: "Включение MFA", tone: "success" },
  "admin.mfa_disable": { label: "Отключение MFA", tone: "warning" },
};

export function AuditPage() {
  const [page, setPage] = useState(0);
  const limit = 50;
  const { data, isLoading } = useAuditLog({
    limit,
    offset: page * limit,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  return (
    <div className="space-y-6">
      <header>
        <span className="label">Аудит</span>
        <h1 className="font-display text-3xl font-bold mt-2">Audit Log</h1>
        <p className="text-sm text-fg-muted mt-1">
          История всех изменений: создание / отзыв / логины / действия админов.
        </p>
      </header>

      <Card>
        <CardBody className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Время</TH>
                <TH>Действие</TH>
                <TH>Админ</TH>
                <TH>Объект</TH>
                <TH>IP</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading && (
                <TR>
                  <TD colSpan={5} className="text-center text-fg-subtle py-8">
                    Загрузка…
                  </TD>
                </TR>
              )}
              {!isLoading && data?.items.length === 0 && (
                <TR>
                  <TD
                    colSpan={5}
                    className="text-center text-fg-subtle py-12"
                  >
                    <FileText size={28} className="mx-auto mb-2 opacity-50" />
                    Записей пока нет.
                  </TD>
                </TR>
              )}
              {data?.items.map((item) => {
                const meta = ACTION_LABEL[item.action];
                return (
                  <TR key={item.id}>
                    <TD className="text-xs text-fg-muted whitespace-nowrap">
                      {new Date(item.created_at).toLocaleString("ru")}
                    </TD>
                    <TD>
                      {meta ? (
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                      ) : (
                        <span className="font-mono text-xs text-fg-muted">
                          {item.action}
                        </span>
                      )}
                    </TD>
                    <TD className="text-sm">
                      {item.actor_email ?? (
                        <span className="text-fg-subtle">—</span>
                      )}
                    </TD>
                    <TD className="text-xs text-fg-muted font-mono">
                      {item.target_type && item.target_id
                        ? `${item.target_type}:${item.target_id.slice(0, 8)}`
                        : "—"}
                    </TD>
                    <TD className="text-xs text-fg-muted font-mono">
                      {item.ip ?? "—"}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      {data && data.total > limit && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-fg-subtle">
            {page * limit + 1}–{Math.min((page + 1) * limit, data.total)} из{" "}
            {data.total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft size={14} />
              Назад
            </Button>
            <div className="text-sm text-fg-muted">
              {page + 1} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперёд
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
